// src/utils/interactiveContent.ts
export interface InteractiveElement {
  text: string;
  category: string;
  identifier: string;
  originalText: string;
}

export interface FollowUpQuestion {
  text: string;
  prompt: string;
  category: string;
}

// One item inside a [PLACES_RESULT] block. The AI emits the bare
// minimum (name + reasoning + optional category hint); the frontend
// grounds each item against Google Places via the placeSearch Cloud
// Function to get place_id, distance, rating, etc.
// One image the AI explicitly wants to embed inline. Marker shape:
//
//   [IMAGE](person:Albrecht Dürer)
//   [IMAGE](place:Brandenburg Gate)
//   [IMAGE](architecture:Gothic)
//   [IMAGE](event:Fall of the Berlin Wall)
//
// Identifier is a human-readable subject the frontend can resolve
// against Wikipedia or Google Places. The frontend renders an
// inline image card; if lookup fails, it gracefully drops to
// nothing (no broken-image placeholder).
export interface EntityImageRef {
  /** Same category enum as inline chips. Drives the lookup
   *  strategy — `place` can fall back to Google Places, others
   *  use Wikipedia. */
  category: string;
  /** Subject as the AI named it. Used as the Wikipedia article
   *  title (URL-encoded) or the Places query string. */
  identifier: string;
}

// One item inside a [TOUR_THEMES] block — a selectable walking-tour
// theme the AI offers when the conversation is in awaiting_focus
// state. The user taps a theme; the frontend sends "Let's go with
// [name]" as a user message; the AI then emits a CONVERSATION_STATE
// marker to flip the tour into generation.
export interface TourThemeItem {
  /** Short title shown as the card heading. */
  name: string;
  /** One-sentence description shown below the title. */
  description?: string;
  /** Rough total distance estimate in km. Optional — older models
   *  may not include it; UI hides the field if absent. */
  distance_km?: number;
  /** Rough total duration estimate in minutes. Optional. */
  duration_min?: number;
  /** Optional emoji or single-letter glyph the UI can use as an
   *  accent. Pure flair, never required. */
  glyph?: string;
}

// One item inside a [MUSEUM_OPTIONS] block — a selectable nearby
// museum the AI offers when a museum_guide conversation is in
// awaiting_museum state. The user taps one; the frontend sends
// "I'm at the [name]." as a user message; the AI then captures it
// via a CONVERSATION_STATE marker and asks guided vs free roam.
export interface MuseumOptionItem {
  /** Museum name shown as the row heading. */
  name: string;
  /** Short address / area line. Optional. */
  vicinity?: string;
  /** Distance from the user in meters. Optional. */
  distance_m?: number;
  /** Optional emoji accent (🎨 🏛️ 🔬 …). */
  glyph?: string;
}

export interface PlacesResultItem {
  /** Place name as the AI knows it. Used for Places Text Search. */
  name: string;
  /** Why this place fits the user's query — shown verbatim as
   *  microcopy under the place name in the rendered card. */
  reasoning?: string;
  /** Optional category/type hint to disambiguate names that exist
   *  in multiple forms (e.g. "Strassenbräu" — beer_garden vs cafe). */
  category?: string;
}

// One [NAVIGATE_TO] block — a single specific place the AI is
// directing the user toward with walking-route intent. Distinct
// from PLACES_RESULT (which is a list of options to choose
// from): NAVIGATE_TO is "go here, here's how." Rendered inline
// in chat as a NavigateToCard with a tappable bottom-sheet map.
//
// The AI emits this when the four-part MANDATORY EMISSION RULE
// in the prompt is satisfied: response recommends a named place,
// user location is available, coordinates are confident, walking
// distance ≤ 1.5 km.
//
//   [NAVIGATE_TO]
//   {"name":"Palatine Hill","lat":41.8893,"lng":12.4870,"reasoning":"Just up the path from the Forum, fewer crowds than the Colosseum"}
//   [/NAVIGATE_TO]
//
// place_id is optional — when present, the Google Maps deep
// link uses destination_place_id for a precise pin match
// rather than falling back to coordinate-only routing.
export interface NavigateTarget {
  /** Display name of the destination. Shown as the card title
   *  and the bottom-sheet header. */
  name: string;
  /** Latitude of the destination, signed decimal degrees. */
  lat: number;
  /** Longitude of the destination, signed decimal degrees. */
  lng: number;
  /** Optional one-sentence rationale for why this place — shown
   *  as microcopy under the title on the teaser card. */
  reasoning?: string;
  /** Optional Google Places place_id. When present, the Maps
   *  deep link uses destination_place_id for a precise routing
   *  match. */
  place_id?: string;
}

// Phase 5E — Then-and-Now block.
//
// Two images of the same subject across time. The AI emits when
// the place under discussion has visibly changed (war damage,
// urban renewal, reconstruction, excavation, restoration). Both
// images are Wikipedia/Commons-resolved client-side.
//
// Wire format:
//
//   [THEN_AND_NOW]
//   {
//     "subject": "Frauenkirche",
//     "then": {
//       "wikipedia_title": "Frauenkirche Dresden ruin 1965",
//       "year": 1965,
//       "caption": "Twenty years after the firebombing; a war memorial of rubble"
//     },
//     "now": {
//       "wikipedia_title": "Frauenkirche Dresden",
//       "year": 2005,
//       "caption": "Reconstructed; black blocks salvaged from the original visible in the new facade"
//     }
//   }
//   [/THEN_AND_NOW]
export interface ThenAndNowSide {
  /** Canonical Wikipedia article title for image resolution.
   *  Same lookup pipeline as [IMAGE](...) markers. */
  wikipedia_title: string;
  /** Year or year range as a display string. The AI may pass
   *  numbers (1989) or expressions ("c. 1850", "Republic"); we
   *  show whatever it gives us. */
  year: string | number;
  /** One-sentence caption shown beneath this image. */
  caption: string;
}

export interface ThenAndNowBlock {
  subject: string;
  then: ThenAndNowSide;
  now: ThenAndNowSide;
}

// Phase 5E — Timeline block.
//
// Horizontal scrollable timeline of historical eras for a layered
// site. Each era is tappable to expand; expanded view shows the
// era's summary and optional key features. When the user taps an
// era's "tell me more" affordance, the chip sends a chat message
// asking the AI to drill into that era.
//
// Negative year values represent BCE. year_end null means "to
// present day". The renderer is presentation-only; no date math
// other than display formatting.
//
//   [TIMELINE]
//   {
//     "subject": "Roman Forum",
//     "eras": [
//       {"label":"Republic","year_start":-509,"year_end":-27,"summary":"…","key_features":["Curia Julia","Rostra"]},
//       {"label":"Empire","year_start":-27,"year_end":476,"summary":"…","key_features":[]},
//       {"label":"Cow Field","year_start":1500,"year_end":1803,"summary":"…","key_features":[]}
//     ]
//   }
//   [/TIMELINE]
export interface TimelineEra {
  /** Short label shown on the timeline dot. ≤24 chars. */
  label: string;
  /** Year the era began. Negative = BCE. May be null when
   *  unknown (rare; AI usually has at least an approximate
   *  bound). */
  year_start: number | null;
  /** Year the era ended. null = "to present day". */
  year_end: number | null;
  /** 1-2 sentence summary shown when the era expands. */
  summary: string;
  /** Optional bullet list of specific features/people associated
   *  with this era. The AI uses this when something concrete and
   *  pointable existed in the era. */
  key_features?: string[];
}

export interface TimelineBlock {
  subject: string;
  eras: TimelineEra[];
}

// Phase 5E — Artwork Zoom block.
//
// An image with annotated regions of interest. For paintings,
// frescoes, reliefs, or any image where the AI's claim is about
// specific details inside the frame. The AI emits normalized
// region coordinates (0..1 of width / height); the renderer
// overlays them as subtle highlight rectangles. Tap → opens a
// zoom view focused on that region with the AI's note.
//
//   [ARTWORK_ZOOM]
//   {
//     "wikipedia_title": "The Garden of Earthly Delights",
//     "regions": [
//       {"label":"John the Baptist","x":0.34,"y":0.41,"width":0.08,"height":0.12,"note":"Hand pointing toward central panel — Bosch's directional cue"},
//       {"label":"Pink fountain","x":0.78,"y":0.28,"width":0.12,"height":0.18,"note":"Crystalline forms that recur across Bosch's work"}
//     ]
//   }
//   [/ARTWORK_ZOOM]
//
// Coordinate convention: x and y are the TOP-LEFT corner of the
// region as a fraction of the image's natural dimensions. width
// and height are also fractions. Out-of-range values are
// clamped at render time; the AI is expected to stay within
// 0..1 but we don't trust it absolutely.
export interface ArtworkZoomRegion {
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  note: string;
}

export interface ArtworkZoomBlock {
  wikipedia_title: string;
  regions: ArtworkZoomRegion[];
}

export interface ParsedContent {
  text: string;
  elements: InteractiveElement[];
  followUpQuestions: FollowUpQuestion[];
}

// Parse markdown-style interactive elements and follow-up questions from AI response
export const parseInteractiveContent = (content: string): ParsedContent => {
  const elements: InteractiveElement[] = [];
  const followUpQuestions: FollowUpQuestion[] = [];

  // Regex to match [DisplayText](category:identifier) pattern
  const interactiveRegex = /\[([^\]]+)\]\(([^:]+):([^)]+)\)/g;

  let match;
  while ((match = interactiveRegex.exec(content)) !== null) {
    elements.push({
      text: match[1], // DisplayText
      category: match[2], // category (person, place, event, etc.)
      identifier: match[3], // identifier
      originalText: match[0], // Full matched text
    });
  }

  // Parse follow-up questions from the content
  // Look for [FOLLOW_UP_QUESTIONS]...[/FOLLOW_UP_QUESTIONS] blocks
  const followUpRegex =
    /\[FOLLOW_UP_QUESTIONS\](.*?)\[\/FOLLOW_UP_QUESTIONS\]/s;
  const followUpMatch = content.match(followUpRegex);

  if (followUpMatch) {
    try {
      const questionsJson = followUpMatch[1].trim();
      const questionsData = JSON.parse(questionsJson);

      if (Array.isArray(questionsData)) {
        questionsData.forEach((q: any) => {
          if (
            q.text &&
            q.prompt &&
            typeof q.text === "string" &&
            typeof q.prompt === "string"
          ) {
            followUpQuestions.push({
              text: q.text,
              prompt: q.prompt,
              category: q.category || "general",
            });
          }
        });
      }
    } catch (error) {
      console.warn("Failed to parse follow-up questions:", error);
    }
  }

  return {
    text: content,
    elements,
    followUpQuestions,
  };
};

// Remove follow-up questions and other server-applied marker blocks
// from content for display. Markers are emitted by the AI and parsed
// server-side (state updates, museum highlights) — by the time the
// message lands in the conversation log they've already been stripped
// from `fullContent`. This client-side strip is defense in depth, and
// also cleans up any markers that flickered through during SSE
// streaming before the full content was finalized.
export const cleanContentForDisplay = (content: string): string => {
  if (!content) return "";

  // First remove well-formed FOLLOW_UP_QUESTIONS blocks
  let cleaned = content.replace(
    /\[FOLLOW_UP_QUESTIONS\][\s\S]*?\[\/FOLLOW_UP_QUESTIONS\]/g,
    ""
  );

  // Fallback: if the start tag leaked without a closing tag, strip everything after it
  cleaned = cleaned.replace(/\[FOLLOW_UP_QUESTIONS\][\s\S]*/g, "");

  // Remove any stray closing tags that might remain
  cleaned = cleaned.replace(/\[\/FOLLOW_UP_QUESTIONS\]/g, "");

  // Strip CONVERSATION_STATE markers (state-machine transitions for
  // mode-aware conversations like museum_guide). The server has
  // already applied them to the conversation doc; the marker should
  // never reach the user's screen.
  cleaned = cleaned.replace(
    /\[CONVERSATION_STATE\][\s\S]*?\[\/CONVERSATION_STATE\]/g,
    ""
  );
  cleaned = cleaned.replace(/\[CONVERSATION_STATE\][\s\S]*/g, "");
  cleaned = cleaned.replace(/\[\/CONVERSATION_STATE\]/g, "");

  // Strip MUSEUM_HIGHLIGHTS markers (the structured exhibit list the
  // AI emits when it generates the guided-mode tour). Same reasoning.
  cleaned = cleaned.replace(
    /\[MUSEUM_HIGHLIGHTS\][\s\S]*?\[\/MUSEUM_HIGHLIGHTS\]/g,
    ""
  );
  cleaned = cleaned.replace(/\[MUSEUM_HIGHLIGHTS\][\s\S]*/g, "");
  cleaned = cleaned.replace(/\[\/MUSEUM_HIGHLIGHTS\]/g, "");

  // Strip MUSEUM_OPTIONS markers (the nearby-museum picker offered
  // during awaiting_museum). Rendered as a tappable card by
  // InteractiveContent, never as raw text.
  cleaned = cleaned.replace(
    /\[MUSEUM_OPTIONS\][\s\S]*?\[\/MUSEUM_OPTIONS\]/g,
    ""
  );
  cleaned = cleaned.replace(/\[MUSEUM_OPTIONS\][\s\S]*/g, "");
  cleaned = cleaned.replace(/\[\/MUSEUM_OPTIONS\]/g, "");

  // Strip MAP_REFINE markers (map-exploration refinement handoff —
  // the server applies these to the conversation doc; the map
  // re-generates from the snapshot).
  cleaned = cleaned.replace(
    /\[MAP_REFINE\][\s\S]*?\[\/MAP_REFINE\]/g,
    ""
  );
  cleaned = cleaned.replace(/\[MAP_REFINE\][\s\S]*/g, "");
  cleaned = cleaned.replace(/\[\/MAP_REFINE\]/g, "");

  // Strip WALKING_TOUR_UPDATE markers (in-flight tour modifications
  // — add/skip waypoint, etc.). Server has already applied them to
  // the conversation doc by the time the message lands; this is
  // defense in depth + cleanup of mid-stream chunks.
  cleaned = cleaned.replace(
    /\[WALKING_TOUR_UPDATE\][\s\S]*?\[\/WALKING_TOUR_UPDATE\]/g,
    ""
  );
  cleaned = cleaned.replace(/\[WALKING_TOUR_UPDATE\][\s\S]*/g, "");
  cleaned = cleaned.replace(/\[\/WALKING_TOUR_UPDATE\]/g, "");

  // Strip PLACES_RESULT markers (structured "find me X" recommendations).
  // The block holds JSON with a list of place names + reasoning that the
  // frontend grounds against Google Places for tap-through. Removed from
  // the visible text — the rendered card replaces it.
  cleaned = cleaned.replace(
    /\[PLACES_RESULT\][\s\S]*?\[\/PLACES_RESULT\]/g,
    ""
  );
  cleaned = cleaned.replace(/\[PLACES_RESULT\][\s\S]*/g, "");
  cleaned = cleaned.replace(/\[\/PLACES_RESULT\]/g, "");

  // Strip TOUR_THEMES markers (selectable walking-tour theme picks
  // for the awaiting_focus → generating handoff). Same treatment —
  // the rendered card replaces the block, the marker should never
  // reach the user's screen.
  cleaned = cleaned.replace(
    /\[TOUR_THEMES\][\s\S]*?\[\/TOUR_THEMES\]/g,
    ""
  );
  cleaned = cleaned.replace(/\[TOUR_THEMES\][\s\S]*/g, "");
  cleaned = cleaned.replace(/\[\/TOUR_THEMES\]/g, "");

  // Strip [IMAGE](category:identifier) markers — they're rendered
  // as inline image cards by InteractiveContent. The text form
  // shouldn't show in the prose; only the resolved image card
  // should appear.
  cleaned = cleaned.replace(/\[IMAGE\]\([^:)]+:[^)]+\)/g, "");

  // Strip [NAVIGATE_TO] markers — the AI emits these when its
  // mandatory routing rule fires ("you should head to X, it's
  // walkable"). InteractiveContent mounts a NavigateToCard for
  // the parsed block; the raw JSON shouldn't reach the user's
  // screen. Was silently leaking before this patch because
  // cleanContentForDisplay never stripped them — affected every
  // chat response that emitted a NAVIGATE_TO.
  cleaned = cleaned.replace(
    /\[NAVIGATE_TO\][\s\S]*?\[\/NAVIGATE_TO\]/g,
    ""
  );
  cleaned = cleaned.replace(/\[NAVIGATE_TO\][\s\S]*/g, "");
  cleaned = cleaned.replace(/\[\/NAVIGATE_TO\]/g, "");

  // Strip [VISIT_CANDIDATES] markers — server-side multi-landmark
  // fanout signal for photo visits. Processed by the upload
  // pipeline before reaching the user's screen; this defensive
  // strip prevents leakage if the block ever sneaks through into
  // the message_doc unchanged.
  cleaned = cleaned.replace(
    /\[VISIT_CANDIDATES\][\s\S]*?\[\/VISIT_CANDIDATES\]/g,
    ""
  );
  cleaned = cleaned.replace(/\[VISIT_CANDIDATES\][\s\S]*/g, "");
  cleaned = cleaned.replace(/\[\/VISIT_CANDIDATES\]/g, "");

  // Phase 5E — strip THEN_AND_NOW / TIMELINE / ARTWORK_ZOOM blocks.
  // Same defensive pattern as the other blocks: full block,
  // unterminated start-tag tail, and stray closing tag. The
  // rendered cards replace the raw text; the markers must
  // never reach the user's screen.
  cleaned = cleaned.replace(
    /\[THEN_AND_NOW\][\s\S]*?\[\/THEN_AND_NOW\]/g,
    ""
  );
  cleaned = cleaned.replace(/\[THEN_AND_NOW\][\s\S]*/g, "");
  cleaned = cleaned.replace(/\[\/THEN_AND_NOW\]/g, "");

  cleaned = cleaned.replace(
    /\[TIMELINE\][\s\S]*?\[\/TIMELINE\]/g,
    ""
  );
  cleaned = cleaned.replace(/\[TIMELINE\][\s\S]*/g, "");
  cleaned = cleaned.replace(/\[\/TIMELINE\]/g, "");

  cleaned = cleaned.replace(
    /\[ARTWORK_ZOOM\][\s\S]*?\[\/ARTWORK_ZOOM\]/g,
    ""
  );
  cleaned = cleaned.replace(/\[ARTWORK_ZOOM\][\s\S]*/g, "");
  cleaned = cleaned.replace(/\[\/ARTWORK_ZOOM\]/g, "");

  return cleaned.trim();
};

// Parse a [PLACES_RESULT] block out of an AI message, if present.
// Returns the items array, or [] if no complete block is found.
//
// The block format:
//   [PLACES_RESULT]
//   { "items": [
//     { "name": "...", "reasoning": "...", "category": "..." },
//     ...
//   ] }
//   [/PLACES_RESULT]
//
// During SSE streaming the closing tag may not have arrived yet — in
// that case we return [] and the renderer keeps showing the prose
// without the card. Once the block completes, parsing succeeds and
// the card renders. JSON parse errors also yield [] (defensive against
// AI emitting malformed JSON mid-stream or hallucinating).
export const parsePlacesResults = (content: string): PlacesResultItem[] => {
  if (!content) return [];
  const m = content.match(
    /\[PLACES_RESULT\]([\s\S]*?)\[\/PLACES_RESULT\]/
  );
  if (!m) return [];
  try {
    const json = JSON.parse(m[1].trim()) as { items?: unknown };
    if (!json || !Array.isArray(json.items)) return [];
    return json.items
      .filter(
        (it): it is { name: string; reasoning?: string; category?: string } =>
          !!it &&
          typeof it === 'object' &&
          typeof (it as { name?: unknown }).name === 'string' &&
          ((it as { name: string }).name as string).trim().length > 0
      )
      .map((it) => ({
        name: it.name.trim(),
        reasoning:
          typeof it.reasoning === 'string' ? it.reasoning.trim() : undefined,
        category:
          typeof it.category === 'string' ? it.category.trim() : undefined,
      }));
  } catch (err) {
    console.warn('Failed to parse PLACES_RESULT block:', err);
    return [];
  }
};

// Parse a [TOUR_THEMES] block — selectable walking-tour theme picks.
// Format mirrors PLACES_RESULT for consistency:
//   [TOUR_THEMES]
//   {"items":[
//     {"name":"Medieval Nürnberg","description":"Half-timbered lanes…","distance_km":2.5,"duration_min":90,"glyph":"🏰"},
//     ...
//   ]}
//   [/TOUR_THEMES]
// Returns [] when no block is present, when JSON is malformed, or
// when the items array is empty / lacks names.
// Parse all [IMAGE](category:identifier) markers from content,
// preserving order. Used by InteractiveContent to render inline
// image cards. Returns [] when there are no markers.
//
// Identifier may contain spaces, dots, hyphens — anything except
// the closing paren. Category is a single token (matches the
// existing chip categories: person, place, event, architecture,
// concept, period, painting).
export const parseEntityImages = (content: string): EntityImageRef[] => {
  if (!content) return [];
  const out: EntityImageRef[] = [];
  const re = /\[IMAGE\]\(([^:)]+):([^)]+)\)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    const category = m[1].trim();
    const identifier = m[2].trim();
    if (category.length > 0 && identifier.length > 0) {
      out.push({ category, identifier });
    }
  }
  return out;
};

export const parseTourThemes = (content: string): TourThemeItem[] => {
  if (!content) return [];
  const m = content.match(/\[TOUR_THEMES\]([\s\S]*?)\[\/TOUR_THEMES\]/);
  if (!m) return [];
  try {
    const json = JSON.parse(m[1].trim()) as { items?: unknown };
    if (!json || !Array.isArray(json.items)) return [];
    return json.items
      .filter(
        (it): it is {
          name: string;
          description?: string;
          distance_km?: number;
          duration_min?: number;
          glyph?: string;
        } =>
          !!it &&
          typeof it === 'object' &&
          typeof (it as { name?: unknown }).name === 'string' &&
          ((it as { name: string }).name as string).trim().length > 0
      )
      .map((it) => ({
        name: it.name.trim(),
        description:
          typeof it.description === 'string'
            ? it.description.trim()
            : undefined,
        distance_km:
          typeof it.distance_km === 'number' && isFinite(it.distance_km)
            ? it.distance_km
            : undefined,
        duration_min:
          typeof it.duration_min === 'number' && isFinite(it.duration_min)
            ? Math.round(it.duration_min)
            : undefined,
        glyph:
          typeof it.glyph === 'string' && it.glyph.trim().length > 0
            ? it.glyph.trim()
            : undefined,
      }));
  } catch (err) {
    console.warn('Failed to parse TOUR_THEMES block:', err);
    return [];
  }
};

// Parse a [MUSEUM_OPTIONS] block — selectable nearby museums offered
// during the awaiting_museum state of a museum_guide conversation.
//   [MUSEUM_OPTIONS]
//   {"items":[
//     {"name":"Rijksmuseum","vicinity":"Museumstraat 1","distance_m":120,"glyph":"🎨"},
//     ...
//   ]}
//   [/MUSEUM_OPTIONS]
// Returns [] when no block is present, JSON is malformed, or items
// lack names — same degradation contract as parseTourThemes.
export const parseMuseumOptions = (content: string): MuseumOptionItem[] => {
  if (!content) return [];
  const m = content.match(/\[MUSEUM_OPTIONS\]([\s\S]*?)\[\/MUSEUM_OPTIONS\]/);
  if (!m) return [];
  try {
    const json = JSON.parse(m[1].trim()) as { items?: unknown };
    if (!json || !Array.isArray(json.items)) return [];
    return json.items
      .filter(
        (it): it is {
          name: string;
          vicinity?: string;
          distance_m?: number;
          glyph?: string;
        } =>
          !!it &&
          typeof it === 'object' &&
          typeof (it as { name?: unknown }).name === 'string' &&
          ((it as { name: string }).name as string).trim().length > 0
      )
      .map((it) => ({
        name: it.name.trim(),
        vicinity:
          typeof it.vicinity === 'string' && it.vicinity.trim().length > 0
            ? it.vicinity.trim()
            : undefined,
        distance_m:
          typeof it.distance_m === 'number' && isFinite(it.distance_m)
            ? Math.round(it.distance_m)
            : undefined,
        glyph:
          typeof it.glyph === 'string' && it.glyph.trim().length > 0
            ? it.glyph.trim()
            : undefined,
      }));
  } catch (err) {
    console.warn('Failed to parse MUSEUM_OPTIONS block:', err);
    return [];
  }
};

// Phase 5E — parse a [THEN_AND_NOW] block. Returns null when the
// block is absent, the JSON is malformed, or required fields are
// missing. Both `then` and `now` are required; the renderer's
// degraded behavior (one side fails Wikipedia lookup) is
// handled at render time, not here.
export const parseThenAndNow = (content: string): ThenAndNowBlock | null => {
  if (!content) return null;
  const m = content.match(/\[THEN_AND_NOW\]([\s\S]*?)\[\/THEN_AND_NOW\]/);
  if (!m) return null;
  try {
    const json = JSON.parse(m[1].trim()) as Record<string, unknown>;
    if (!json || typeof json !== 'object') return null;
    const subject = typeof json.subject === 'string' ? json.subject.trim() : '';
    const then = json.then as Record<string, unknown> | undefined;
    const now = json.now as Record<string, unknown> | undefined;
    if (!subject || !then || !now) return null;

    const validateSide = (s: Record<string, unknown>): ThenAndNowSide | null => {
      const wt = typeof s.wikipedia_title === 'string' ? s.wikipedia_title.trim() : '';
      const yr = s.year;
      const cap = typeof s.caption === 'string' ? s.caption.trim() : '';
      if (!wt || !cap) return null;
      if (typeof yr !== 'string' && typeof yr !== 'number') return null;
      return { wikipedia_title: wt, year: yr, caption: cap };
    };
    const thenSide = validateSide(then);
    const nowSide = validateSide(now);
    if (!thenSide || !nowSide) return null;
    return { subject, then: thenSide, now: nowSide };
  } catch (err) {
    console.warn('Failed to parse THEN_AND_NOW block:', err);
    return null;
  }
};

// Phase 5E — parse a [TIMELINE] block. Returns null when absent
// or malformed. Eras with no label are dropped. Year bounds are
// permitted to be null (open-ended); the renderer handles the
// display logic.
export const parseTimeline = (content: string): TimelineBlock | null => {
  if (!content) return null;
  const m = content.match(/\[TIMELINE\]([\s\S]*?)\[\/TIMELINE\]/);
  if (!m) return null;
  try {
    const json = JSON.parse(m[1].trim()) as Record<string, unknown>;
    if (!json || typeof json !== 'object') return null;
    const subject = typeof json.subject === 'string' ? json.subject.trim() : '';
    const rawEras = Array.isArray(json.eras) ? json.eras : [];
    if (!subject || rawEras.length === 0) return null;

    const eras: TimelineEra[] = rawEras
      .filter(
        (e: unknown): e is Record<string, unknown> =>
          !!e && typeof e === 'object'
      )
      .map((e) => {
        const label = typeof e.label === 'string' ? e.label.trim() : '';
        const summary = typeof e.summary === 'string' ? e.summary.trim() : '';
        return {
          label,
          summary,
          year_start: typeof e.year_start === 'number' ? e.year_start : null,
          year_end: typeof e.year_end === 'number' ? e.year_end : null,
          key_features:
            Array.isArray(e.key_features)
              ? (e.key_features as unknown[])
                  .filter((kf): kf is string => typeof kf === 'string')
                  .map((kf) => kf.trim())
                  .filter((kf) => kf.length > 0)
              : undefined,
        };
      })
      .filter((e) => e.label.length > 0 && e.summary.length > 0);
    if (eras.length === 0) return null;
    return { subject, eras };
  } catch (err) {
    console.warn('Failed to parse TIMELINE block:', err);
    return null;
  }
};

// Phase 5E — parse an [ARTWORK_ZOOM] block. Coordinates are
// clamped to [0,1] at parse time so the renderer can trust
// them. Regions outside the frame are dropped. Regions with
// zero/negative width or height are dropped (would be invisible).
export const parseArtworkZoom = (content: string): ArtworkZoomBlock | null => {
  if (!content) return null;
  const m = content.match(/\[ARTWORK_ZOOM\]([\s\S]*?)\[\/ARTWORK_ZOOM\]/);
  if (!m) return null;
  try {
    const json = JSON.parse(m[1].trim()) as Record<string, unknown>;
    if (!json || typeof json !== 'object') return null;
    const wt =
      typeof json.wikipedia_title === 'string'
        ? json.wikipedia_title.trim()
        : '';
    const rawRegions = Array.isArray(json.regions) ? json.regions : [];
    if (!wt || rawRegions.length === 0) return null;

    const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

    const regions: ArtworkZoomRegion[] = rawRegions
      .filter(
        (r: unknown): r is Record<string, unknown> =>
          !!r && typeof r === 'object'
      )
      .map((r) => {
        const label = typeof r.label === 'string' ? r.label.trim() : '';
        const note = typeof r.note === 'string' ? r.note.trim() : '';
        const x = typeof r.x === 'number' ? clamp01(r.x) : 0;
        const y = typeof r.y === 'number' ? clamp01(r.y) : 0;
        const w = typeof r.width === 'number' ? clamp01(r.width) : 0;
        const h = typeof r.height === 'number' ? clamp01(r.height) : 0;
        return { label, note, x, y, width: w, height: h };
      })
      .filter(
        (r) =>
          r.label.length > 0 &&
          r.note.length > 0 &&
          r.width > 0 &&
          r.height > 0 &&
          r.x + r.width <= 1 &&
          r.y + r.height <= 1
      );
    if (regions.length === 0) return null;
    return { wikipedia_title: wt, regions };
  } catch (err) {
    console.warn('Failed to parse ARTWORK_ZOOM block:', err);
    return null;
  }
};

// Parse a [NAVIGATE_TO] block out of an AI message. Returns null
// when the block is absent, the JSON is malformed, or required
// numeric coordinates are missing/invalid. Used by
// InteractiveContent to mount a NavigateToCard inline when the
// AI fires its mandatory routing rule.
//
//   [NAVIGATE_TO]
//   {"name":"Palatine Hill","lat":41.8893,"lng":12.4870,"reasoning":"…","place_id":"ChIJ…"}
//   [/NAVIGATE_TO]
//
// Validation: name must be non-empty; lat/lng must be finite
// numbers in valid range. reasoning and place_id are optional.
export const parseNavigateTo = (content: string): NavigateTarget | null => {
  if (!content) return null;
  const m = content.match(/\[NAVIGATE_TO\]([\s\S]*?)\[\/NAVIGATE_TO\]/);
  if (!m) return null;
  try {
    const json = JSON.parse(m[1].trim()) as Record<string, unknown>;
    if (!json || typeof json !== 'object') return null;
    const name = typeof json.name === 'string' ? json.name.trim() : '';
    const lat = typeof json.lat === 'number' ? json.lat : NaN;
    const lng = typeof json.lng === 'number' ? json.lng : NaN;
    if (
      !name ||
      !Number.isFinite(lat) ||
      !Number.isFinite(lng) ||
      lat < -90 ||
      lat > 90 ||
      lng < -180 ||
      lng > 180
    ) {
      return null;
    }
    const reasoning =
      typeof json.reasoning === 'string' && json.reasoning.trim().length > 0
        ? json.reasoning.trim()
        : undefined;
    const place_id =
      typeof json.place_id === 'string' && json.place_id.trim().length > 0
        ? json.place_id.trim()
        : undefined;
    return { name, lat, lng, reasoning, place_id };
  } catch (err) {
    console.warn('Failed to parse NAVIGATE_TO block:', err);
    return null;
  }
};

// Replace interactive markup with placeholder spans that can be rendered as buttons
export const convertToInteractiveHTML = (content: string): string => {
  const interactiveRegex = /\[([^\]]+)\]\(([^:]+):([^)]+)\)/g;

  return content.replace(
    interactiveRegex,
    (match, text, category, identifier) => {
      // Create a data attribute with the interaction info
      return `<span class="interactive-element" data-text="${text}" data-category="${category}" data-identifier="${identifier}">${text}</span>`;
    }
  );
};

// Generate follow-up message for an interactive element
export const generateFollowUpMessage = (
  element: InteractiveElement,
  currentLocation?: string,
  context?: string
): string => {
  const locationContext = currentLocation
    ? ` in the context of ${currentLocation}`
    : "";
  const additionalContext = context ? ` ${context}` : "";

  switch (element.category) {
    case "person":
      return `Tell me more about ${element.text}${locationContext}. Focus on their historical significance, key achievements, and connection to this location.${additionalContext}`;

    case "place":
      return `Provide detailed information about ${element.text}${locationContext}. Include its history, significance, and interesting facts for visitors.${additionalContext}`;

    case "event":
      return `Explain the ${element.text} event${locationContext}. Cover what happened, when it occurred, key figures involved, and its historical impact.${additionalContext}`;

    case "architecture":
      return `Describe the architectural features and significance of ${element.text}${locationContext}. Include design style, construction details, and artistic elements.${additionalContext}`;

    case "concept":
      return `Explain the concept of ${element.text}${locationContext}. Provide background, cultural significance, and how it relates to this location.${additionalContext}`;

    case "period":
      return `Tell me about the ${element.text} period${locationContext}. Cover the timeframe, major events, cultural changes, and lasting impact.${additionalContext}`;

    default:
      return `Tell me more about ${element.text}${locationContext}. Provide detailed information and context for visitors.${additionalContext}`;
  }
};

// Extract current location context from recent messages
export const extractLocationContext = (
  recentMessages: any[]
): string | undefined => {
  // Look for location references in recent user or assistant messages
  const locationKeywords = [
    "at",
    "visiting",
    "location",
    "here",
    "this place",
    "monument",
    "building",
    "museum",
  ];

  for (
    let i = recentMessages.length - 1;
    i >= Math.max(0, recentMessages.length - 3);
    i--
  ) {
    const message = recentMessages[i];
    const messageText =
      typeof message.content === "string" ? message.content : "";

    // Simple heuristic to find location mentions
    const words = messageText.toLowerCase().split(/\s+/);
    for (let j = 0; j < words.length; j++) {
      if (locationKeywords.some((keyword) => words[j].includes(keyword))) {
        // Extract surrounding words as potential location context
        const contextStart = Math.max(0, j - 2);
        const contextEnd = Math.min(words.length, j + 3);
        const context = words.slice(contextStart, contextEnd).join(" ");

        if (context.length > 10) {
          // Minimum context length
          return context;
        }
      }
    }
  }

  return undefined;
};
